import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Check, Link2, Plus, Trash2, X } from "lucide-react";

import {
  useConfirmRelation,
  useProductRelations,
  useRelateProducts,
  useRemoveRelation,
  useSearch,
} from "@/api/queries";
import type {
  Product,
  ProductRelation,
  ProductRelationType,
  RelationCertainty,
  ValueSource,
} from "@/api/types";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { StatusBadge } from "@/components/StatusBadge";
import {
  ASSERTABLE_CERTAINTIES,
  ASSERTABLE_SOURCES,
  CERTAINTY_LABELS,
  groupRelations,
  RELATION_CHOICES,
  relationLabel,
  SOURCE_LABELS,
} from "./relation-display";

/**
 * Compatibilité et références liées (TICKET V1.5-07, ADR 0013).
 *
 * Sa propre carte depuis V1.5-07 : avec sept types, des directions, des
 * certitudes et un formulaire, les enfouir dans la carte commerciale rendrait
 * les deux illisibles.
 *
 * Trois choses qu'elle fait exprès :
 *
 * **Elle dit la direction en français.** Une même ligne se lit « Accessoire
 * pour » d'un bout et « Accessoires » de l'autre. L'utilisateur ne voit jamais
 * `ACCESSORY_FOR`.
 *
 * **Elle sépare ce qui est enregistré de ce qui est déduit.** Deux listes, et
 * jamais fondues : une structure qui les mélange finit par être aplatie
 * quelque part, et c'est là que l'inférence devient un fait.
 *
 * **Elle ne dit jamais « incompatible » par défaut.** Une absence de lien est
 * une absence d'information. Seule une relation `INCOMPATIBLE` affirme un non.
 */
export function ProductRelationsCard({ product }: { product: Product }) {
  const [showInferred, setShowInferred] = useState(false);
  const { data, isLoading } = useProductRelations(product.id, {
    includeInferred: showInferred,
  });
  const [adding, setAdding] = useState(false);

  const registered = data?.registered ?? [];
  const groups = groupRelations(registered);
  const inferred = data?.inferred ?? [];

  return (
    <Card>
      <CardHeader
        title="Compatibilité et références liées"
        subtitle="Ce qui va avec ce produit, ce qui le remplace, et d'où HD-Vault le sait"
        action={
          <Button size="sm" variant="secondary" onClick={() => setAdding((open) => !open)}>
            {adding ? (
              <>
                <X className="size-4" aria-hidden />
                Annuler
              </>
            ) : (
              <>
                <Plus className="size-4" aria-hidden />
                Ajouter
              </>
            )}
          </Button>
        }
      />
      <CardBody className="flex flex-col gap-3">
        {adding && (
          <RelationForm productId={product.id} onDone={() => setAdding(false)} />
        )}

        {isLoading && <p className="text-[12px] text-ink-subtle">Chargement…</p>}

        {!isLoading && registered.length === 0 && (
          <p className="text-[12px] text-ink-subtle">
            Aucun lien enregistré. Tant qu'il n'y en a pas, HD-Vault répond « je n'ai pas
            l'information » — jamais « ces deux produits ne vont pas ensemble ».
          </p>
        )}

        {groups.map((group) => (
          <section key={group.key} className="flex flex-col gap-1">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
              {group.title}
            </h3>
            <ul className="flex flex-col divide-y divide-border">
              {group.items.map((relation) => (
                <RelationRow key={relation.id} productId={product.id} relation={relation} />
              ))}
            </ul>
          </section>
        ))}

        <div className="border-t border-border pt-2">
          <label className="flex items-center gap-2 text-[12px] text-ink-subtle">
            <input
              type="checkbox"
              checked={showInferred}
              onChange={(event) => setShowInferred(event.target.checked)}
            />
            Proposer des compatibilités probables d'après les caractéristiques communes
          </label>

          {showInferred && inferred.length === 0 && (
            <p className="pt-1 text-[12px] text-ink-subtle">
              Rien à proposer : aucun autre produit ne partage d'attribut d'interface.
            </p>
          )}

          {showInferred && inferred.length > 0 && (
            <ul className="flex flex-col gap-1 pt-2">
              {inferred.map((item) => (
                <li
                  key={item.other_product_id}
                  className="flex flex-col gap-0.5 rounded-md border border-dashed border-border px-2.5 py-1.5"
                >
                  <span className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/products/${item.other_product_id}`}
                      className="text-[13px] text-ink hover:underline"
                    >
                      {item.other_product_name}
                    </Link>
                    <StatusBadge tone="reserved">Probable</StatusBadge>
                  </span>
                  <span className="text-[11px] text-ink-subtle">
                    Déduit, non enregistré — {item.reason}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function RelationRow({
  productId,
  relation,
}: {
  productId: string;
  relation: ProductRelation;
}) {
  const confirm = useConfirmRelation(productId);
  const remove = useRemoveRelation(productId);
  const certainty = CERTAINTY_LABELS[relation.certainty];

  return (
    <li className="flex flex-col gap-1 py-2 first:pt-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2">
          <ArrowRight className="size-3.5 shrink-0 text-ink-subtle" aria-hidden />
          <Link
            to={`/products/${relation.other_product_id}`}
            className="truncate text-[14px] text-ink hover:underline"
          >
            {relation.other_product_name ?? "Produit"}
          </Link>
          {relation.other_product_part_number && (
            <span className="text-[11px] text-ink-subtle">
              {relation.other_product_part_number}
            </span>
          )}
        </span>
        <span className="flex flex-wrap items-center gap-2">
          <StatusBadge tone={certainty.tone}>{certainty.label}</StatusBadge>
          <span className="text-[11px] text-ink-subtle">
            Source : {SOURCE_LABELS[relation.source]}
            {relation.confirmed_by_human ? " · confirmé" : " · non confirmé"}
          </span>
        </span>
      </div>

      <p className="pl-5 text-[12px] text-ink-subtle">
        {relationLabel(relation.relation_type, relation.reversed)}
        {relation.symmetric && " · lien symétrique"}
      </p>

      {relation.source_reference && (
        <p className="pl-5 text-[11px] text-ink-subtle">
          Référence : {relation.source_reference}
        </p>
      )}
      {relation.note && <p className="pl-5 text-[11px] text-ink-subtle">{relation.note}</p>}

      <div className="flex gap-2 pl-5">
        {!relation.confirmed_by_human && (
          <Button
            size="sm"
            variant="ghost"
            loading={confirm.isPending}
            onClick={() => confirm.mutate(relation.id)}
          >
            <Check className="size-3.5" aria-hidden />
            Confirmer
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          loading={remove.isPending}
          onClick={() => remove.mutate(relation.id)}
        >
          <Trash2 className="size-3.5" aria-hidden />
          Retirer
        </Button>
      </div>
    </li>
  );
}

/**
 * Le formulaire.
 *
 * Le produit cible se **cherche** ; il ne se tape pas. Demander un UUID à
 * quelqu'un est la meilleure façon d'obtenir une relation vers le mauvais
 * produit, ou vers rien du tout.
 */
function RelationForm({ productId, onDone }: { productId: string; onDone: () => void }) {
  const save = useRelateProducts(productId);
  const [relationType, setRelationType] = useState<ProductRelationType>("WORKS_WITH");
  const [query, setQuery] = useState("");
  const [target, setTarget] = useState<{ id: string; title: string } | null>(null);
  const [certainty, setCertainty] = useState<RelationCertainty>("LIKELY");
  const [source, setSource] = useState<ValueSource>("HUMAN");
  const [reference, setReference] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: results } = useSearch(target ? "" : query);
  const candidates = (results?.products ?? []).filter((hit) => hit.id !== productId).slice(0, 6);

  async function submit() {
    if (!target) return;
    setError(null);
    try {
      await save.mutateAsync({
        to_product_id: target.id,
        relation_type: relationType,
        source,
        certainty,
        source_reference: reference,
        confirm: certainty === "VERIFIED",
      });
      onDone();
    } catch (caught) {
      setError((caught as { message?: string })?.message ?? "Enregistrement refusé.");
    }
  }

  return (
    <form
      className="flex flex-col gap-3 rounded-md border border-border bg-surface-sunken p-3"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <label className="flex flex-col gap-1 text-[12px] text-ink-subtle">
        Ce produit…
        <select
          value={relationType}
          onChange={(event) => setRelationType(event.target.value as ProductRelationType)}
          className="rounded-md border border-border bg-surface px-2 py-1.5 text-[14px] text-ink"
        >
          {RELATION_CHOICES.map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </select>
      </label>

      <div className="flex flex-col gap-1">
        <label className="flex flex-col gap-1 text-[12px] text-ink-subtle">
          Produit lié
          <input
            value={target ? target.title : query}
            onChange={(event) => {
              setTarget(null);
              setQuery(event.target.value);
            }}
            placeholder="Chercher par nom, modèle ou code…"
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-[14px] text-ink"
          />
        </label>
        {!target && candidates.length > 0 && (
          <ul className="flex flex-col gap-0.5">
            {candidates.map((hit) => (
              <li key={hit.id}>
                <button
                  type="button"
                  onClick={() => setTarget({ id: hit.id, title: hit.title })}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-[13px] text-ink hover:bg-surface-hover"
                >
                  <Link2 className="size-3.5 shrink-0 text-ink-subtle" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{hit.title}</span>
                  <span className="text-[11px] text-ink-subtle">{hit.subtitle}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <label className="flex flex-col gap-1 text-[12px] text-ink-subtle">
          Certitude
          <select
            value={certainty}
            onChange={(event) => setCertainty(event.target.value as RelationCertainty)}
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-[14px] text-ink"
          >
            {ASSERTABLE_CERTAINTIES.map((item) => (
              <option key={item} value={item}>
                {CERTAINTY_LABELS[item].label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-[12px] text-ink-subtle">
          Source
          <select
            value={source}
            onChange={(event) => setSource(event.target.value as ValueSource)}
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-[14px] text-ink"
          >
            {ASSERTABLE_SOURCES.map((item) => (
              <option key={item} value={item}>
                {SOURCE_LABELS[item]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex min-w-0 flex-1 flex-col gap-1 text-[12px] text-ink-subtle">
          Référence (facultatif)
          <input
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            placeholder="fiche technique p. 12"
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-[14px] text-ink"
          />
        </label>
      </div>

      {error && (
        <p className="flex items-center gap-2 text-[12px] text-status-out">
          <AlertTriangle className="size-3.5" aria-hidden />
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <Button type="submit" size="sm" loading={save.isPending} disabled={!target}>
          Enregistrer
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Annuler
        </Button>
      </div>
    </form>
  );
}
